/*
//  * Copyright (C) 2024 - present Instructure, Inc.
 *
 * This file is part of Canvas.
 *
 * Canvas is free software: you can redistribute it and/or modify it under
 * the terms of the GNU Affero General Public License as published by the Free
 * Software Foundation, version 3 of the License.
 *
 * Canvas is distributed in the hope that it will be useful, but WITHOUT ANY
 * WARRANTY; without even the implied warranty of MERCHANTABILITY or FITNESS FOR
 * A PARTICULAR PURPOSE. See the GNU Affero General Public License for more
 * details.
 *
 * You should have received a copy of the GNU Affero General Public License along
 * with this program. If not, see <http://www.gnu.org/licenses/>.
 */
import React, {useCallback, useEffect, useLayoutEffect, useState} from 'react'
import {useEditor} from '@craftjs/core'
import {CloseButton, IconButton} from '@instructure/ui-buttons'
import {Flex} from '@instructure/ui-flex'
import {Heading} from '@instructure/ui-heading'
import {Modal} from '@instructure/ui-modal'
import {View} from '@instructure/ui-view'
import {enhanceUserContent} from '@instructure/canvas-rce'
import BlockEditorView from '../../BlockEditorView'
import {LATEST_BLOCK_DATA_VERSION} from '../../utils/transformations'
import {IconDesktop, IconTablet, IconMobile} from '../../assets/internal-icons'

import {useScope as createI18nScope} from '@canvas/i18n'

const I18n = createI18nScope('block-editor')

type ViewSize = 'desktop' | 'tablet' | 'mobile'

type FrameType = {
  frame: string
  clippath: string
  frameStyle: React.CSSProperties
  contentStyle: React.CSSProperties
}

const frames: Record<ViewSize, FrameType> = {
  desktop: {
    frame: `<svg viewBox="0 0 1305 686" style="enable-background:new 0 0 1305 686;">
<style type="text/css">
	.st0{clip-path:url(#SVGID_00000174599394661615561530000008462144386194787244_);}
	.st1{fill:#939393;}
</style>
<g>
	<defs>
		<rect id="SVGID_1_" width="1305" height="686"/>
	</defs>
	<clipPath id="SVGID_00000042729567313813646720000012339662135225360265_">
		<use xlink:href="#SVGID_1_"  style="overflow:visible;"/>
	</clipPath>
	<g style="clip-path:url(#SVGID_00000042729567313813646720000012339662135225360265_);">
		<path class="st1" d="M758.5,652.3c0,0.1-0.1,0.2-0.2,0.3c-0.1,0.1-0.3,0.2-0.5,0.2H538.5c-0.2,0-0.4-0.1-0.5-0.2
			c-0.1-0.1-0.2-0.2-0.2-0.4c-0.1-0.3,0.1-0.6,0.3-0.8c0,0,0.1,0,0.1-0.1c0,0,0,0,0,0c0,0,0.1,0,0.1,0c0.1,0,0.1,0,0.2,0h219.3
			c0.1,0,0.1,0,0.2,0c0,0,0,0,0,0c0,0,0,0,0,0s0,0,0,0c0,0,0,0,0,0c0,0,0,0,0,0c0,0,0,0,0.1,0h0c0,0,0,0,0,0c0,0,0,0,0,0
			c0,0,0,0,0,0h0l0,0c0,0,0.1,0.1,0.1,0.1C758.6,651.7,758.7,652,758.5,652.3z"/>
		<path class="st1" d="M757,651.1c-0.2,0-0.3,0.1-0.5,0.2c0,0,0,0,0,0h1.3c0.1,0,0.1,0,0.2,0c0,0,0,0,0,0c0,0,0,0,0,0
			C757.7,651.1,757.4,651.1,757,651.1z M758.1,651.3C758.1,651.3,758.1,651.3,758.1,651.3C758.1,651.3,758.1,651.3,758.1,651.3z
			 M757,651.1c-0.2,0-0.3,0.1-0.5,0.2c0,0,0,0,0,0h1.3c0.1,0,0.1,0,0.2,0c0,0,0,0,0,0c0,0,0,0,0,0
			C757.7,651.1,757.4,651.1,757,651.1z M758.1,651.3C758.1,651.3,758.1,651.3,758.1,651.3C758.1,651.3,758.1,651.3,758.1,651.3z
			 M757,651.1c-0.2,0-0.3,0.1-0.5,0.2c0,0,0,0,0,0h1.3c0.1,0,0.1,0,0.2,0c0,0,0,0,0,0c0,0,0,0,0,0
			C757.7,651.1,757.4,651.1,757,651.1z M758.1,651.3C758.1,651.3,758.1,651.3,758.1,651.3C758.1,651.3,758.1,651.3,758.1,651.3z
			 M757,651.1c-0.2,0-0.3,0.1-0.5,0.2c0,0,0,0,0,0h1.3c0.1,0,0.1,0,0.2,0c0,0,0,0,0,0c0,0,0,0,0,0
			C757.7,651.1,757.4,651.1,757,651.1z M758.1,651.3C758.1,651.3,758.1,651.3,758.1,651.3C758.1,651.3,758.1,651.3,758.1,651.3z
			 M759.4,652.5c0-0.1-0.1-0.2-0.1-0.2c-0.1-0.1-0.1-0.2-0.2-0.3c0,0,0,0-0.1-0.1c0,0-0.1-0.1-0.1-0.1c-0.1-0.1-0.3-0.3-0.5-0.4
			c0,0-0.1,0-0.1-0.1c0,0,0,0,0,0h0l0,0c0,0,0,0,0,0c0,0,0,0,0,0c0,0,0,0,0,0h0c0,0,0,0-0.1,0c0,0,0,0,0,0c0,0,0,0,0,0c0,0,0,0,0,0
			c-0.3-0.1-0.7-0.2-1-0.2c-0.2,0-0.3,0.1-0.5,0.2c0,0,0,0,0,0c-0.2,0.1-0.3,0.3-0.3,0.6c0,0.4,0.3,0.7,0.7,0.7c0,0,0,0,0.1,0
			c0.2,0,0.3,0,0.5,0.1c0,0,0,0,0,0c0,0,0.1,0,0.1,0.1c0.2,0.1,0.3,0.2,0.4,0.4c0,0,0,0.1,0,0.1c0,0,0,0.1,0,0.1c0,0,0,0.1,0,0.1
			v1.5c0,0,0,0,0,0.1c0,0.6-0.1,1.1-0.2,1.7c0,0,0,0,0,0c-0.1,0.2,0,0.5,0.1,0.6c0.1,0.2,0.4,0.3,0.6,0.3h0.6
			c0.1-0.2,0.1-0.4,0.2-0.6c0.1-0.3,0.1-0.6,0.2-0.9h0c0-0.2,0-0.3,0.1-0.5c0,0,0,0,0-0.1c0,0,0-0.1,0-0.1c0-0.1,0-0.3,0-0.4
			c0-0.1,0-0.1,0-0.2v-1.5c0-0.3,0-0.5-0.1-0.7C759.4,652.6,759.4,652.6,759.4,652.5L759.4,652.5z M757,651c-0.2,0-0.3,0.1-0.5,0.2
			c0,0,0,0,0,0h1.3c0.1,0,0.1,0,0.2,0c0,0,0,0,0,0c0,0,0,0,0,0C757.7,651.1,757.4,651,757,651z M758.1,651.3
			C758.1,651.3,758.1,651.3,758.1,651.3C758.1,651.3,758.1,651.3,758.1,651.3z M757,651c-0.2,0-0.3,0.1-0.5,0.2c0,0,0,0,0,0h1.3
			c0.1,0,0.1,0,0.2,0c0,0,0,0,0,0c0,0,0,0,0,0C757.7,651.1,757.4,651,757,651z M758.1,651.3C758.1,651.3,758.1,651.3,758.1,651.3
			C758.1,651.3,758.1,651.3,758.1,651.3z M757,651c-0.2,0-0.3,0.1-0.5,0.2c0,0,0,0,0,0h1.3c0.1,0,0.1,0,0.2,0c0,0,0,0,0,0
			c0,0,0,0,0,0C757.7,651.1,757.4,651,757,651z M758.1,651.3C758.1,651.3,758.1,651.3,758.1,651.3
			C758.1,651.3,758.1,651.3,758.1,651.3z"/>
		<path class="st1" d="M758.5,652.3c0,0.1-0.1,0.2-0.2,0.3c-0.1,0.1-0.3,0.2-0.5,0.2H538.5c-0.2,0-0.4-0.1-0.5-0.2
			c-0.1-0.1-0.2-0.2-0.2-0.4c-0.1-0.3,0.1-0.6,0.3-0.8c0,0,0.1,0,0.1-0.1c0,0,0,0,0,0c0,0,0,0,0,0c0.3-0.2,0.7-0.2,1.1-0.2H757
			c0.4,0,0.7,0.1,1,0.2c0,0,0,0,0,0c0,0,0,0,0,0c0,0,0,0,0,0c0,0,0,0,0.1,0h0c0,0,0,0,0,0c0,0,0,0,0,0c0,0,0,0,0,0h0l0,0
			c0,0,0.1,0.1,0.1,0.1C758.6,651.7,758.7,652,758.5,652.3L758.5,652.3z"/>
		<path class="st1" d="M539.8,651.2C539.8,651.2,539.7,651.2,539.8,651.2c-0.1-0.1-0.3-0.2-0.5-0.2c-0.4,0-0.8,0.1-1.1,0.2
			c0,0,0,0,0,0c0,0,0,0,0,0c0,0-0.1,0-0.1,0.1c-0.5,0.3-0.9,0.7-1.1,1.2c0,0,0,0.1-0.1,0.1c-0.1,0.2-0.1,0.5-0.1,0.7v1.5
			c0,0.1,0,0.1,0,0.2c0,0.1,0,0.3,0,0.4c0,0,0,0.1,0,0.1c0,0,0,0,0,0.1c0,0.1,0,0.3,0,0.4c0,0,0,0,0,0.1h0c0,0.3,0.1,0.6,0.2,0.9
			c0,0.2,0.1,0.4,0.2,0.6h0.6c0.2,0,0.5-0.1,0.6-0.3c0.1-0.2,0.2-0.4,0.1-0.6c0,0,0,0,0,0c-0.2-0.5-0.2-1.1-0.2-1.7c0,0,0,0,0-0.1
			v-1.5c0,0,0-0.1,0-0.1c0,0,0-0.1,0-0.1c0,0,0,0,0-0.1c0,0,0-0.1,0-0.1c0,0,0-0.1,0.1-0.1c0.1-0.1,0.2-0.2,0.3-0.3
			c0,0,0.1-0.1,0.1-0.1c0.1-0.1,0.3-0.1,0.5-0.1c0,0,0,0,0,0c0.4,0,0.7-0.3,0.7-0.7C540,651.6,539.9,651.4,539.8,651.2L539.8,651.2z
			"/>
		<path class="st1" d="M537.2,657.7C537.2,657.7,537.2,657.7,537.2,657.7C537.2,657.7,537.2,657.7,537.2,657.7L537.2,657.7z
			 M537.2,657.7C537.2,657.7,537.2,657.7,537.2,657.7C537.2,657.7,537.2,657.7,537.2,657.7L537.2,657.7z M537.2,657.7
			C537.2,657.7,537.2,657.7,537.2,657.7C537.2,657.7,537.2,657.7,537.2,657.7L537.2,657.7z M537.2,657.7
			C537.2,657.7,537.2,657.7,537.2,657.7C537.2,657.7,537.2,657.7,537.2,657.7L537.2,657.7z M758.5,656.2c-0.3,0-0.6,0.2-0.7,0.5
			c0,0,0,0,0,0c-0.3,1.1-0.8,2.1-1.6,2.9c-1.7,1.9-4.2,3-6.9,3H547c-2.7,0-5.2-1.1-6.9-3c-0.8-0.9-1.3-1.9-1.6-2.9c0,0,0,0,0,0
			c-0.1-0.3-0.4-0.5-0.7-0.5h-0.9c0,0.3,0.1,0.6,0.2,0.9c0,0.2,0.1,0.4,0.2,0.6c0,0,0,0.1,0,0.1c0,0,0,0,0,0c0,0.1,0.1,0.2,0.1,0.3
			c0.1,0.1,0.1,0.3,0.2,0.4s0.1,0.3,0.2,0.4c0.1,0.1,0.1,0.2,0.2,0.3c0,0,0,0.1,0,0.1c0.1,0.1,0.1,0.2,0.2,0.3c0,0,0,0,0,0
			c0.1,0.1,0.1,0.2,0.2,0.3c0,0,0.1,0.1,0.1,0.1c0.1,0.1,0.1,0.2,0.2,0.3c0.1,0.1,0.2,0.2,0.3,0.3c2,2.2,4.9,3.5,8,3.5h202.4
			c3.1,0,6-1.3,8-3.5c0.1-0.1,0.2-0.3,0.3-0.4c0.1-0.1,0.2-0.3,0.3-0.4c0.1-0.1,0.1-0.2,0.2-0.3c0.1-0.1,0.1-0.2,0.1-0.2
			c0.1-0.1,0.1-0.2,0.2-0.3c0.1-0.1,0.1-0.2,0.2-0.3c0-0.1,0.1-0.1,0.1-0.2c0.1-0.3,0.2-0.5,0.3-0.8c0.1-0.2,0.1-0.4,0.2-0.6
			c0.1-0.3,0.1-0.6,0.2-0.9H758.5z M1298.8,654.3c0.5-0.2,0.9-0.3,1.4-0.5C1299.8,654,1299.3,654.1,1298.8,654.3z M537.2,657.7
			C537.2,657.7,537.2,657.7,537.2,657.7C537.2,657.7,537.2,657.7,537.2,657.7L537.2,657.7z M537.2,657.7
			C537.2,657.7,537.2,657.7,537.2,657.7C537.2,657.7,537.2,657.7,537.2,657.7L537.2,657.7z M537.2,657.7
			C537.2,657.7,537.2,657.7,537.2,657.7C537.2,657.7,537.2,657.7,537.2,657.7L537.2,657.7z M537.2,657.7
			C537.2,657.7,537.2,657.7,537.2,657.7C537.2,657.7,537.2,657.7,537.2,657.7L537.2,657.7z"/>
		<path class="st1" d="M1303.9,653.2C1303.9,653.2,1303.8,653.2,1303.9,653.2c-0.1-0.1-0.1-0.1-0.1-0.1c0,0,0-0.1,0-0.1
			c-0.7-1.2-2.1-2-3.6-2h-112.1c0.1-1.1,0.2-2.2,0.2-3.3V34.1c0-18.8-16.9-34.1-37.6-34.1h-997C132.9,0,116,15.3,116,34.1v613.7
			c0,1.1,0.1,2.2,0.2,3.3H4.1c-1.3,0-2.6,0.6-3.3,1.6c0,0-0.1,0.1-0.1,0.1c-0.5,0.6-0.7,1.3-0.7,2.1c0,0.9,0.5,1.7,1.3,2.1
			c35.6,18.8,107.3,28.4,213,28.4H1090c105.7,0,177.4-9.5,213-28.4c0.8-0.4,1.3-1.2,1.3-2.1C1304.3,654.3,1304.2,653.7,1303.9,653.2
			z M117.5,34.1c0-18,16.2-32.6,36.1-32.6h997c19.9,0,36.1,14.6,36.1,32.6v613.7c0,1.1-0.1,2.2-0.2,3.3h-9.6
			c0.1-0.1,0.1-0.2,0.2-0.4c0.1-0.2,0.2-0.4,0.2-0.6c0.3-0.8,0.4-1.7,0.4-2.5V34.1c0-13.6-12.2-24.6-27.2-24.6h-997
			c-15,0-27.2,11.1-27.2,24.6v613.5c0,0.2,0,0.4,0,0.7c0,0.2,0,0.4,0.1,0.6c0,0.2,0.1,0.4,0.1,0.6c0.1,0.3,0.2,0.6,0.3,0.9
			c0.1,0.2,0.2,0.4,0.3,0.6c0,0,0,0.1,0.1,0.1h-9.6c-0.1-1.1-0.2-2.2-0.2-3.3V34.1z M1302.3,655.6c-35.4,18.7-106.8,28.2-212.3,28.2
			H214.3c-105.5,0-176.9-9.5-212.3-28.2c-0.3-0.2-0.5-0.4-0.5-0.7c0-0.3,0-0.5,0.1-0.8c0.5,0.2,1.1,0.4,1.7,0.7
			c0.2,0.1,0.4,0.2,0.6,0.2c0.2,0.1,0.5,0.2,0.7,0.3c0.4,0.2,0.9,0.4,1.3,0.5c0.3,0.1,0.6,0.2,0.9,0.4c1.5,0.6,3,1.2,3.5,1.4
			c0.1,0,0.2,0.1,0.3,0.1h526.4c-0.1-0.2-0.1-0.4-0.2-0.6c-0.1-0.3-0.2-0.6-0.2-0.9H10.9c-0.6-0.3-2-0.8-3.4-1.4
			c-0.1-0.1-0.3-0.1-0.4-0.2c-0.1-0.1-0.3-0.1-0.4-0.2c-0.1-0.1-0.3-0.1-0.4-0.2c-0.2-0.1-0.4-0.2-0.6-0.2c-0.6-0.2-1.1-0.4-1.6-0.6
			c-0.1-0.1-0.3-0.1-0.4-0.2c-0.3-0.1-0.6-0.3-0.9-0.4c0.4-0.2,0.9-0.3,1.3-0.3h532.8c0.2-0.5,0.6-0.9,1.1-1.2c0,0,0.1,0,0.1-0.1
			c0,0,0,0,0,0c0,0,0,0,0,0c0.3-0.1,0.7-0.2,1.1-0.2H129c-0.1-0.1-0.2-0.2-0.2-0.3c-0.1-0.1-0.2-0.3-0.2-0.4
			c-0.1-0.1-0.1-0.3-0.2-0.5c-0.1-0.3-0.2-0.5-0.2-0.7c0-0.2-0.1-0.3-0.1-0.5c0-0.2-0.1-0.4-0.1-0.5c0-0.2,0-0.4,0-0.5V34.1
			c0-12.8,11.5-23.1,25.7-23.1h997c14.2,0,25.7,10.4,25.7,23.1v613.5c0,0.7-0.1,1.3-0.3,2c-0.1,0.2-0.1,0.3-0.2,0.5
			c-0.1,0.2-0.2,0.3-0.2,0.4c-0.1,0.2-0.2,0.3-0.3,0.4c0,0-0.1,0.1-0.1,0.1h-1.1H757c0.4,0,0.7,0.1,1,0.2c0,0,0,0,0,0c0,0,0,0,0,0
			c0,0,0,0,0,0c0,0,0,0,0.1,0h0c0,0,0,0,0,0c0,0,0,0,0,0c0,0,0,0,0,0h0l0,0c0,0,0,0,0,0c0,0,0.1,0,0.1,0.1c0.2,0.1,0.3,0.2,0.5,0.4
			c0,0,0.1,0.1,0.1,0.1c0,0,0,0,0.1,0.1c0.1,0.1,0.1,0.2,0.2,0.3c0,0.1,0.1,0.2,0.1,0.2h540.8c0.7,0,1.3,0.2,1.8,0.6
			c-0.5,0.2-1.1,0.4-1.8,0.6c-0.4,0.2-0.9,0.3-1.4,0.5c-0.1,0-0.3,0.1-0.4,0.1c-0.3,0.1-0.6,0.2-0.8,0.3c-1.7,0.6-3.4,1.1-4.2,1.5
			H759.5c0,0.3-0.1,0.6-0.2,0.9c-0.1,0.2-0.1,0.4-0.2,0.6h534c0.2,0.1,0.4,0.1,0.6,0c0.6-0.3,2.5-0.9,4.4-1.5
			c0.8-0.3,1.5-0.5,2.2-0.7c0.2-0.1,0.5-0.2,0.7-0.2c0.7-0.2,1.3-0.5,1.8-0.7c0,0.1,0,0.2,0,0.3
			C1302.8,655.1,1302.6,655.4,1302.3,655.6L1302.3,655.6z"/>
		<path class="st1" d="M758.5,652.3c0,0.1-0.1,0.2-0.2,0.3c-0.1,0.1-0.3,0.2-0.5,0.2H538.5c-0.2,0-0.4-0.1-0.5-0.2
			c-0.1-0.1-0.2-0.2-0.2-0.4c-0.1-0.3,0.1-0.6,0.3-0.8c0,0,0.1,0,0.1-0.1c0,0,0,0,0,0c0,0,0,0,0,0c0.3-0.2,0.7-0.2,1.1-0.2H757
			c0.4,0,0.7,0.1,1,0.2c0,0,0,0,0,0c0,0,0,0,0,0c0,0,0,0,0,0c0,0,0,0,0.1,0h0c0,0,0,0,0,0c0,0,0,0,0,0c0,0,0,0,0,0h0l0,0
			c0,0,0.1,0.1,0.1,0.1C758.6,651.7,758.7,652,758.5,652.3L758.5,652.3z"/>
		<path class="st1" d="M759.5,652.7c0,0,0-0.1-0.1-0.1c0-0.1-0.1-0.2-0.1-0.2c-0.1-0.1-0.1-0.2-0.2-0.3c0,0,0,0-0.1-0.1
			c0,0-0.1-0.1-0.1-0.1c-0.1-0.1-0.3-0.3-0.5-0.4c0,0-0.1,0-0.1-0.1c0,0,0,0,0,0h0l0,0c0,0,0,0,0,0c0,0,0,0,0,0c0,0,0,0,0,0h0
			c0,0,0,0-0.1,0c0,0,0,0,0,0c0,0,0,0,0,0c0,0,0,0,0,0s0,0,0,0c0,0,0,0,0,0c0,0-0.1,0-0.2,0H538.5c-0.1,0-0.1,0-0.2,0
			c0,0-0.1,0-0.1,0c0,0,0,0,0,0c0,0-0.1,0-0.1,0.1c-0.5,0.3-0.9,0.7-1.1,1.2c0,0.1,0,0.1-0.1,0.2c-0.1,0.2-0.1,0.5-0.1,0.7v1.5
			c0,0.1,0,0.1,0,0.2c0,0.1,0,0.3,0,0.4c0,0,0,0.1,0,0.1c0,0,0,0,0,0.1c0,0.1,0,0.3,0,0.4c0,0,0,0,0,0.1c0,0.3,0.1,0.6,0.2,0.9
			c0,0.2,0.1,0.4,0.2,0.6c0,0,0,0.1,0,0.1c0,0,0,0,0,0c0,0.1,0.1,0.2,0.1,0.3c0,0.1,0.1,0.3,0.2,0.4s0.1,0.3,0.2,0.4
			c0.1,0.1,0.1,0.2,0.2,0.3c0,0,0,0.1,0,0.1c0.1,0.1,0.1,0.2,0.2,0.3c0,0,0,0,0,0c0.1,0.1,0.1,0.2,0.2,0.3c0,0,0.1,0.1,0.1,0.1
			c0.1,0.1,0.1,0.2,0.2,0.3c0.1,0.1,0.2,0.2,0.3,0.3c2,2.2,4.9,3.5,8,3.5h202.4c3.1,0,6-1.3,8-3.5c0.1-0.1,0.2-0.3,0.3-0.4
			c0.1-0.1,0.2-0.3,0.3-0.4c0.1-0.1,0.1-0.2,0.2-0.3c0.1-0.1,0.1-0.2,0.1-0.2c0.1-0.1,0.1-0.2,0.2-0.3c0.1-0.1,0.1-0.2,0.2-0.3
			c0-0.1,0.1-0.1,0.1-0.2c0.1-0.3,0.2-0.5,0.3-0.8c0.1-0.2,0.1-0.4,0.2-0.6c0.1-0.3,0.1-0.6,0.2-0.9c0-0.2,0-0.3,0.1-0.5
			c0,0,0,0,0-0.1c0,0,0-0.1,0-0.1c0-0.1,0-0.3,0-0.4c0-0.1,0-0.1,0-0.2v-1.5C759.6,653.2,759.5,652.9,759.5,652.7L759.5,652.7z
			 M756.2,659.7c-1.7,1.9-4.2,3-6.9,3H546.9c-2.7,0-5.2-1.1-6.9-3c-0.8-0.9-1.3-1.9-1.6-2.9c0,0,0,0,0,0c-0.1-0.5-0.2-1.1-0.2-1.7
			c0,0,0,0,0-0.1v-1.5c0,0,0-0.1,0-0.1c0,0,0-0.1,0-0.1c0,0,0,0,0-0.1c0,0,0-0.1,0-0.1c0,0,0-0.1,0.1-0.1c0.1-0.1,0.2-0.2,0.3-0.3
			h219c0.2,0.1,0.3,0.2,0.4,0.4c0,0,0,0.1,0,0.1c0,0,0,0.1,0,0.1c0,0,0,0.1,0,0.1v1.5c0,0,0,0,0,0.1c0,0.6-0.1,1.2-0.2,1.7
			c0,0,0,0,0,0C757.5,657.8,757,658.8,756.2,659.7L756.2,659.7z"/>
	</g>
</g>
</svg>
`,
    clippath: `<svg viewBox="0 0 1048.4 640.1" width="0" height="0">
    <defs>
      <clipPath id="clip-path">
        <path d="M1048.4,23.1v613.5c0,0.7-0.1,1.3-0.4,2c-0.1,0.1-0.1,0.3-0.2,0.5c-0.1,0.2-0.2,0.3-0.2,0.4
          c-0.1,0.2-0.2,0.3-0.3,0.4c0,0.1-0.1,0.1-0.1,0.1H411.4c-0.1,0-0.2,0-0.3,0H1.1C1,640,1,639.9,0.9,639.8c-0.1-0.1-0.2-0.3-0.2-0.4
          c-0.1-0.2-0.1-0.3-0.2-0.5c-0.1-0.3-0.2-0.5-0.2-0.7c0-0.2-0.1-0.3-0.1-0.5c0-0.2,0-0.3-0.1-0.5c0-0.2,0-0.3,0-0.5V23.1
          C0,10.4,11.5,0,25.7,0h997C1036.9,0,1048.4,10.4,1048.4,23.1z"/>
      </clipPath>
    </defs>
</svg>`,
    frameStyle: {
      width: '1305px',
      height: '686px',
    },
    contentStyle: {
      width: '1051px',
      height: '640px',
      left: '125px',
      top: '11px',
    },
  },
  tablet: {
    frame: `<svg width="860" height="1194" viewBox="0 0 410 567" >
<g clip-path="url(#clip0_7656_41436)">
<path d="M195.745 12.175C195.76 12.295 195.775 12.415 195.8 12.53C195.8 12.545 195.81 12.56 195.81 12.575C196.18 14.18 197.675 15.145 199.07 15.145C200.065 15.145 200.895 14.81 201.485 14.235C202.08 13.65 202.42 12.81 202.42 11.8C202.42 11.095 202.175 10.365 201.725 9.76995C201.15 8.99995 200.235 8.44995 199.07 8.44995C197.38 8.44995 195.72 10.11 195.72 11.8C195.72 11.93 195.725 12.055 195.74 12.18C195.74 12.18 195.74 12.18 195.74 12.185L195.745 12.175ZM199.075 13.635C198.315 13.635 197.51 13.12 197.29 12.26C197.285 12.24 197.275 12.22 197.27 12.2C197.24 12.07 197.225 11.935 197.225 11.795C197.225 10.95 198.23 9.94495 199.075 9.94495C199.645 9.94495 200.11 10.185 200.43 10.535C200.75 10.885 200.925 11.35 200.925 11.79C200.925 12.385 200.76 12.85 200.45 13.16C200.135 13.47 199.675 13.635 199.08 13.635H199.075Z" fill="#939393"/>
<path d="M381.95 22.575H27.3C24.09 22.575 21.375 25.205 21.375 28.32V539.555C21.375 542.625 24.035 545.12 27.3 545.12H381.95C385.175 545.12 387.705 542.675 387.705 539.555V28.32C387.705 25.15 385.125 22.575 381.95 22.575ZM386.205 539.555C386.205 541.835 384.335 543.62 381.95 543.62H27.3C24.86 543.62 22.875 541.795 22.875 539.555V28.32C22.875 26.02 24.9 24.075 27.3 24.075H381.95C384.295 24.075 386.205 25.98 386.205 28.32V539.555Z" fill="#939393"/>
<path d="M211.265 12.1701C211.365 12.6501 211.66 13.0151 212.025 13.2401C212.3 13.4101 212.62 13.5001 212.935 13.5001C213.72 13.5001 214.235 13.2051 214.48 12.6451C214.585 12.4051 214.645 12.1251 214.645 11.7851C214.645 10.9451 214.005 10.0701 212.93 10.0701C212.555 10.0701 212.14 10.2951 211.81 10.6201C211.8 10.6251 211.795 10.6351 211.785 10.6401C211.77 10.6551 211.755 10.6751 211.74 10.6951C211.435 11.0201 211.22 11.4201 211.22 11.7851C211.22 11.9201 211.235 12.0451 211.26 12.1651L211.265 12.1701ZM213 11.5801C213.145 11.6151 213.15 11.7551 213.15 11.7901C213.15 11.8851 213.145 11.9501 213.135 11.9851C213.095 11.9901 213.03 12.0001 212.935 12.0001C212.91 12.0001 212.825 11.9951 212.77 11.9351C212.75 11.9151 212.735 11.8851 212.73 11.8501C212.77 11.7801 212.855 11.7001 212.935 11.6351C212.955 11.6151 212.98 11.5951 213 11.5801Z" fill="#939393"/>
<path d="M409.12 79.18V71.535C409.225 71.195 409.33 70.86 409.43 70.52V61.11C409.44 58.945 409.415 53.79 409.44 51.7C409.34 51.345 409.23 50.995 409.12 50.64V30.85C409.12 14.085 395.485 0.45 378.72 0.45H377.715C377.175 0.29 376.635 0.15 376.135 0.025L376.04 0H375.945C374.285 0.015 370.83 0.015 368.305 0.01H357.125L356.81 0.065C356.345 0.185 355.87 0.31 355.405 0.45H30.405C22.365 0.45 14.755 3.635 8.97 9.42C3.185 15.205 0 22.815 0 30.85V535.9C0 552.665 13.64 566.3 30.405 566.3H378.72C386.93 566.3 394.585 563.16 400.28 557.46C405.98 551.76 409.12 544.105 409.12 535.9V100.075C409.225 99.735 409.33 99.4 409.43 99.06V89.65C409.44 87.485 409.415 82.335 409.44 80.24C409.34 79.885 409.23 79.535 409.12 79.18ZM399.22 556.4C393.81 561.815 386.53 564.8 378.72 564.8H30.405C14.465 564.8 1.5 551.835 1.5 535.9V30.85C1.5 23.215 4.53 15.98 10.03 10.48C15.53 4.98 22.765 1.95 30.405 1.95H355.625L355.73 1.92C356.215 1.775 356.705 1.645 357.19 1.52L357.22 1.51H368.305C370.78 1.51 374.155 1.51 375.86 1.5C376.35 1.625 376.87 1.76 377.385 1.915L377.49 1.945H378.715C394.65 1.945 407.615 14.91 407.615 30.845V50.645C407.505 50.995 407.395 51.345 407.295 51.695C407.32 53.82 407.305 58.91 407.305 61.105V70.515C407.405 70.85 407.505 71.185 407.615 71.52V79.18C407.505 79.53 407.395 79.88 407.295 80.23C407.32 82.355 407.305 87.445 407.305 89.64V99.05C407.405 99.385 407.505 99.72 407.615 100.055V535.89C407.615 543.69 404.63 550.975 399.215 556.39L399.22 556.4Z" fill="#939393"/>
</g>
<defs>
<clipPath id="clip0_7656_41436">
<rect width="409.44" height="566.3" fill="white"/>
</clipPath>
</defs>
</svg>
`,
    clippath: `<svg viewBox="0 0 363.3 519.5" width="0" height="0">
      <defs>
      <clipPath id="clip-path" style="transform:scale(2.12); transform-origin: top left">
        <path class="st0" d="M363.3,4.2v511.2c0,2.3-1.9,4.1-4.2,4.1H4.4c-2.4,0-4.4-1.8-4.4-4.1V4.2C0,1.9,2,0,4.4,0h354.7
	      C361.4,0,363.3,1.9,363.3,4.2z"/>
      </clipPath>
      </defs>
  </svg>`,
    frameStyle: {
      width: '860px',
      height: '1194px',
    },
    contentStyle: {
      left: '46px',
      top: '55px',
      width: '764px',
      height: '1064px',
    },
  },
  mobile: {
    frame: `<svg width="383" height="790" viewBox="0 0 190 392">
<g clip-path="url(#clip0_7656_41441)">
<path d="M189.21 87.09V29.195C189.21 13.095 176.115 0 160.015 0H29.98C13.885 0 0.79 13.095 0.79 29.195V50.965C0.325 51.185 0 51.66 0 52.21V63.92C0 64.47 0.325 64.94 0.79 65.165V77.53C0.325 77.75 0 78.225 0 78.775V102.975C0 103.525 0.325 103.995 0.79 104.215V111.43C0.325 111.65 0 112.125 0 112.675V136.875C0 137.425 0.325 137.895 0.79 138.115V362.285C0.79 378.385 13.885 391.48 29.98 391.48H160.01C176.1 391.48 189.2 378.385 189.21 362.285V129.17C189.49 128.92 189.67 128.56 189.67 128.155V88.105C189.67 87.7 189.49 87.34 189.21 87.09ZM187.71 362.285C187.7 377.555 175.275 389.98 160.01 389.98H29.98C14.71 389.98 2.29 377.555 2.29 362.285V29.195C2.29 13.925 14.71 1.5 29.98 1.5H160.015C175.285 1.5 187.71 13.925 187.71 29.195V362.285Z" fill="#939393"/>
<path d="M160.015 5H29.98C23.45 5 17.3 7.55 12.67 12.18C8.04 16.81 5.49 22.955 5.495 29.485V362.575C5.495 376.08 16.48 387.06 29.98 387.06H160.01C173.51 387.06 184.495 376.075 184.5 362.575V29.485C184.5 15.98 173.515 5 160.015 5ZM160.015 385.56H29.98C17.305 385.56 6.995 375.25 6.995 362.575V29.485C6.995 23.355 9.385 17.59 13.73 13.24C18.075 8.895 23.845 6.5 29.98 6.5H160.015C172.69 6.5 183 16.81 183 29.485V362.575C183 375.25 172.68 385.56 160.01 385.56H160.015Z" fill="#939393"/>
<path d="M160.015 12.7749H146.07C144.325 12.7749 142.8 14.0049 142.45 15.7049C141.175 21.8699 135.665 26.3449 129.355 26.3449H58.8849C52.5749 26.3449 47.0699 21.8699 45.7949 15.6999C45.4399 14.0049 43.9199 12.7749 42.1749 12.7749H29.9799C20.9299 12.7749 13.5649 20.1399 13.5649 29.1949V362.285C13.5649 371.34 20.9299 378.705 29.9799 378.705H160.015C169.07 378.705 176.435 371.34 176.435 362.285V29.1949C176.435 20.1399 169.07 12.7749 160.015 12.7749ZM174.935 362.285C174.935 370.51 168.24 377.205 160.015 377.205H29.9799C21.7549 377.205 15.0649 370.51 15.0649 362.285V29.1949C15.0649 20.9699 21.7549 14.2749 29.9799 14.2749H42.1749C43.2099 14.2749 44.1149 15.0049 44.3249 16.0049C45.7449 22.8649 51.8699 27.8399 58.8849 27.8399H129.355C136.375 27.8399 142.5 22.8599 143.92 15.9999C144.13 14.9949 145.03 14.2699 146.07 14.2699H160.015C168.24 14.2699 174.935 20.9649 174.935 29.1899V362.28V362.285Z" fill="#939393"/>
<path d="M84.175 19.3999H102.235C103.565 19.3999 104.65 18.3149 104.65 16.9849C104.65 15.6499 103.565 14.5649 102.235 14.5649H84.175C82.84 14.5649 81.755 15.6499 81.755 16.9849C81.755 18.3149 82.84 19.3999 84.175 19.3999ZM84.175 16.0649H102.235C102.74 16.0649 103.15 16.4749 103.15 16.9849C103.15 17.4899 102.74 17.8999 102.235 17.8999H84.175C83.67 17.8999 83.255 17.4899 83.255 16.9849C83.255 16.4799 83.67 16.0649 84.175 16.0649Z" fill="#939393"/>
<path d="M113.86 20.7651C115.94 20.7651 117.64 19.0651 117.645 16.9751C117.645 14.8851 115.945 13.1851 113.86 13.1851C111.775 13.1851 110.07 14.8851 110.07 16.9751C110.07 19.0651 111.77 20.7651 113.86 20.7651ZM113.86 14.6851C115.12 14.6851 116.145 15.7101 116.145 16.9701C116.145 18.2351 115.115 19.2601 113.86 19.2601C112.605 19.2601 111.57 18.2351 111.57 16.9701C111.57 15.7101 112.6 14.6801 113.86 14.6801V14.6851Z" fill="#939393"/>
</g>
<defs>
<clipPath id="clip0_7656_41441">
<rect width="189.67" height="391.48" fill="white"/>
</clipPath>
</defs>
</svg>
`,
    clippath: `<svg viewBox="0 0 159.9 362.9" preserveAspectRatio="true" width="0" height="0">
        <defs>
          <clipPath id="clip-path" style="transform:scale(2) translateY(8px); transform-origin: top left">
            <path d="M159.9,14.9V348c0,8.2-6.7,14.9-14.9,14.9h-130C6.7,362.9,0,356.2,0,348V14.9C0,6.7,6.7,0,14.9,0h12.2
            c1,0,2,0.7,2.2,1.7c1.4,6.9,7.5,11.8,14.5,11.8h70.5c7,0,13.2-5,14.6-11.8c0.2-1,1.1-1.7,2.2-1.7H145C153.2,0,159.9,6.7,159.9,14.9z
            "/>
        </clipPath>
      </defs>
    </svg>`,
    frameStyle: {
      width: '383px',
      height: '760px',
    },
    contentStyle: {
      top: '13px',
      left: '30px',
      width: '320px',
      height: '757px',
    },
  },
}

const getViewWidth = (viewSize: ViewSize) => {
  return frames[viewSize].frameStyle.width
}

type PreviewModalProps = {
  open: boolean
  onDismiss: () => void
}
const PreviewModal = ({open, onDismiss}: PreviewModalProps) => {
  const {query} = useEditor()
  const [viewSize, setViewSize] = useState<ViewSize>('desktop')
  const [viewRef, setViewRef] = useState<HTMLElement | null>(null)
  const [rendered, setRendered] = useState(false)

  const handleKey = useCallback(
    (event: KeyboardEvent) => {
      if (open && event.key === 'Escape') {
        onDismiss()
      }
    },
    [onDismiss, open],
  )

  const handleRendered = useCallback(() => {
    setRendered(true)
  }, [])

  useEffect(() => {
    if (viewRef && rendered) {
      // @ts-expect-error
      enhanceUserContent(viewRef, {canvasOrigin: window.location.origin})
    }
  }, [viewRef, rendered])

  useEffect(() => {
    document.addEventListener('keydown', handleKey)
    return () => {
      document.removeEventListener('keydown', handleKey)
    }
  }, [handleKey])

  const handleDesktopSize = useCallback(() => {
    setViewSize('desktop')
  }, [])

  const handleTabletSize = useCallback(() => {
    setViewSize('tablet')
  }, [])

  const handleMobileSize = useCallback(() => {
    setViewSize('mobile')
  }, [])

  const frameStyle = () => {
    const styl = {
      position: 'relative',
      top: '0px',
      left: '0px',
      zIndex: 2,
      pointerEvents: 'none',
      ...frames[viewSize].frameStyle,
    } as React.CSSProperties
    return styl
  }

  const viewStyle = () => {
    const styl = {
      position: 'absolute',
      overflowX: 'auto',
      clipPath: 'url(#clip-path)',
      ...frames[viewSize].contentStyle,
    } as React.CSSProperties
    return styl
  }

  return (
    <Modal open={open} size="fullscreen" label={I18n.t('Preview')}>
      <Modal.Header>
        <Flex justifyItems="space-between">
          <Heading level="h2">{I18n.t('Preview')}</Heading>
          <Flex gap="large" direction="row-reverse">
            <CloseButton
              data-testid="preview-modal-close-button"
              offset="small"
              onClick={onDismiss}
              screenReaderLabel={I18n.t('Close')}
            />
            <Flex as="div" gap="small">
              <IconButton
                data-testid="preview-modal-icon-button-desktop"
                size="small"
                onClick={handleDesktopSize}
                screenReaderLabel={I18n.t('Desktop')}
                title={I18n.t('Desktop')}
                withBackground={viewSize === 'desktop'}
                withBorder={viewSize === 'desktop'}
                aria-current={viewSize === 'desktop' ? 'true' : undefined}
                themeOverride={{secondaryBackground: '#E9F2F9', secondaryBorderColor: '#2B7BBC'}}
              >
                <IconDesktop size="x-small" />
              </IconButton>
              <IconButton
                data-testid="preview-modal-icon-button-tablet"
                size="small"
                onClick={handleTabletSize}
                screenReaderLabel={I18n.t('Tablet')}
                title={I18n.t('Tablet')}
                withBackground={viewSize === 'tablet'}
                withBorder={viewSize === 'tablet'}
                aria-current={viewSize === 'tablet' ? 'true' : undefined}
                themeOverride={{secondaryBackground: '#E9F2F9', secondaryBorderColor: '#2B7BBC'}}
              >
                <IconTablet size="x-small" />
              </IconButton>
              <IconButton
                data-testid="preview-modal-icon-button-mobile"
                size="small"
                onClick={handleMobileSize}
                screenReaderLabel={I18n.t('Mobile')}
                title={I18n.t('Mobile')}
                withBackground={viewSize === 'mobile'}
                withBorder={viewSize === 'mobile'}
                aria-current={viewSize === 'mobile' ? 'true' : undefined}
                themeOverride={{secondaryBackground: '#E9F2F9', secondaryBorderColor: '#2B7BBC'}}
              >
                <IconMobile size="x-small" />
              </IconButton>
            </Flex>
          </Flex>
        </Flex>
      </Modal.Header>
      <Modal.Body>
        <View
          as="div"
          position="relative"
          className={`block-editor-previewview ${viewSize}`}
          padding="0"
          margin="0 auto"
          width={getViewWidth(viewSize)}
        >
          <div
            style={frameStyle()}
            dangerouslySetInnerHTML={{
              __html: `${frames[viewSize].clippath}${frames[viewSize].frame}`,
            }}
          />
          <div style={viewStyle()} className="block-editor-view">
            <BlockEditorView
              content={{version: LATEST_BLOCK_DATA_VERSION, blocks: JSON.parse(query.serialize())}}
            />
          </div>
        </View>
      </Modal.Body>
    </Modal>
  )
}
export {PreviewModal, getViewWidth}
